import { apiClient } from './client';
import { createComponentLogger } from '@/lib/utils/logger';
import { phoneSearchValues } from '@/lib/utils/phone';

const logger = createComponentLogger('appointmentsApi');

/**
 * FHIR Appointment Status
 * @see https://www.hl7.org/fhir/valueset-appointmentstatus.html
 */
export type AppointmentStatus =
  | 'proposed'
  | 'pending'
  | 'booked'
  | 'arrived'
  | 'checked-in'
  | 'fulfilled'
  | 'cancelled'
  | 'noshow'
  | 'entered-in-error'
  | 'waitlist';

/**
 * FHIR Appointment Participant Status
 */
export type ParticipantStatus = 'accepted' | 'declined' | 'tentative' | 'needs-action';

/**
 * FHIR Appointment Participant Type
 */
export interface AppointmentParticipant {
  type?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  actor?: {
    reference?: string;
    display?: string;
  };
  required?: 'required' | 'optional' | 'information-only';
  status: ParticipantStatus;
}

/**
 * FHIR Appointment Resource
 * @see https://www.hl7.org/fhir/appointment.html
 */
export interface AppointmentResource {
  resourceType: 'Appointment';
  id: string;
  meta?: {
    versionId?: string;
    lastUpdated?: string;
    source?: string;
    profile?: string[];
  };
  identifier?: Array<{
    system?: string;
    value?: string;
  }>;
  status: AppointmentStatus;
  cancelationReason?: {
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  };
  serviceCategory?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  serviceType?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  specialty?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  appointmentType?: {
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  };
  reasonCode?: Array<{
    coding?: Array<{
      system?: string;
      code?: string;
      display?: string;
    }>;
    text?: string;
  }>;
  reasonReference?: Array<{
    reference?: string;
    display?: string;
  }>;
  priority?: number;
  description?: string;
  supportingInformation?: Array<{
    reference?: string;
    display?: string;
  }>;
  start?: string;
  end?: string;
  minutesDuration?: number;
  slot?: Array<{
    reference?: string;
  }>;
  created?: string;
  comment?: string;
  patientInstruction?: string;
  basedOn?: Array<{
    reference?: string;
  }>;
  participant: AppointmentParticipant[];
  requestedPeriod?: Array<{
    start?: string;
    end?: string;
  }>;
}

/**
 * FHIR Bundle response
 */
export interface AppointmentBundle {
  resourceType: 'Bundle';
  id?: string;
  type: 'searchset' | 'collection';
  total?: number;
  link?: Array<{
    relation: string;
    url: string;
  }>;
  entry?: Array<{
    fullUrl?: string;
    resource: AppointmentResource;
    search?: {
      mode?: 'match' | 'include';
      score?: number;
    };
  }>;
}

/**
 * Parameters for listing appointments
 */
export interface ListAppointmentsParams {
  /** Maximum number of results to return */
  count?: number;
  /** Offset for pagination */
  offset?: number;
  /** Sort order (e.g., '-_lastUpdated' for descending, '_lastUpdated' for ascending) */
  sort?: string;
  /** Include total count in response */
  total?: 'none' | 'estimate' | 'accurate';
  /** Filter by appointment status */
  status?: AppointmentStatus | AppointmentStatus[];
  /** Filter by date range - start */
  dateStart?: string;
  /** Filter by date range - end */
  dateEnd?: string;
  /** Filter by patient reference (e.g., 'Patient/123') */
  patient?: string;
  /** Filter by practitioner reference (e.g., 'Practitioner/456') */
  practitioner?: string;
  /** Filter by actor (participant) reference (e.g., 'Practitioner/456') - more reliable than practitioner */
  actor?: string;
  /** Filter by appointment end time - greater than (e.g., 'gt2025-12-11T14:16:51.000Z') */
  endGt?: string;
  /** Filter by service type */
  serviceType?: string;
  /** Filter by appointment type */
  appointmentType?: string;
  /** Search by identifier */
  identifier?: string;
  /** Search by patient name (FHIR chained search: patient.name) */
  patientName?: string;
  /** Search by patient phone (FHIR chained search: patient.phone) */
  patientPhone?: string;
  /** Include related resources (e.g., 'Appointment:patient', 'Appointment:actor') */
  include?: string[];
}

/**
 * Simplified appointment data for UI
 */
export interface AppointmentListItem {
  id: string;
  status: AppointmentStatus;
  start?: string;
  end?: string;
  duration?: number;
  description?: string;
  comment?: string;
  appointmentType?: string;
  serviceType?: string;
  patientName?: string;
  patientId?: string;
  practitionerName?: string;
  practitionerId?: string;
  encounterId?: string;
  created?: string;
  lastUpdated?: string;
}

/**
 * Result of listing appointments
 */
export interface ListAppointmentsResult {
  appointments: AppointmentListItem[];
  total: number;
  hasNext: boolean;
  hasPrev: boolean;
  /** Included Patient resources (when using _include=Appointment:patient) */
  patients?: Map<string, any>;
  /** Included Practitioner resources (when using _include=Appointment:actor) */
  practitioners?: Map<string, any>;
  /** Map of patient ID to whether they are a new patient (first appointment ever) */
  patientIsNewMap?: Map<string, boolean>;
}

/**
 * Parameters for creating an appointment
 */
export interface CreateAppointmentRequest {
  status: AppointmentStatus;
  start: string;
  end?: string;
  minutesDuration?: number;
  patientId: string;
  practitionerId: string;
  description?: string;
  comment?: string;
  appointmentType?: string;
  serviceType?: string;
  priority?: number;
  reasonCode?: string;
  reasonText?: string;
  /** Optional Encounter ID to link as supportingInformation */
  encounterId?: string;
}

/**
 * Parameters for updating an appointment
 */
export interface UpdateAppointmentRequest {
  status?: AppointmentStatus;
  start?: string;
  end?: string;
  minutesDuration?: number;
  description?: string;
  comment?: string;
  cancelationReason?: string;
  /** Optional Encounter ID to link as supportingInformation (for adding vitals) */
  encounterId?: string;
  /** Optional Practitioner ID to reassign the appointment to a different practitioner */
  practitionerId?: string;
}

/**
 * Extract participant information from appointment
 */
function extractParticipant(
  appointment: AppointmentResource,
  resourceType: 'Patient' | 'Practitioner'
): { id?: string; name?: string } {
  const participant = appointment.participant?.find((p) =>
    p.actor?.reference?.startsWith(`${resourceType}/`)
  );

  if (!participant?.actor) {
    return {};
  }

  const reference = participant.actor.reference || '';
  const id = reference.split('/').pop();
  const name = participant.actor.display;

  return { id, name };
}

/**
 * Transform FHIR Appointment resource to simplified list item
 */
function transformAppointmentToListItem(resource: AppointmentResource): AppointmentListItem {
  const patient = extractParticipant(resource, 'Patient');
  const practitioner = extractParticipant(resource, 'Practitioner');

  // Extract encounter ID from supportingInformation
  let encounterId: string | undefined;
  if (resource.supportingInformation) {
    const encounterRef = resource.supportingInformation.find((info) =>
      info.reference?.startsWith('Encounter/')
    );
    if (encounterRef?.reference) {
      encounterId = encounterRef.reference.replace('Encounter/', '');
    }
  }

  return {
    id: resource.id,
    status: resource.status,
    start: resource.start,
    end: resource.end,
    duration: resource.minutesDuration,
    description: resource.description,
    comment: resource.comment,
    appointmentType:
      resource.appointmentType?.text || resource.appointmentType?.coding?.[0]?.display,
    serviceType: resource.serviceType?.[0]?.text || resource.serviceType?.[0]?.coding?.[0]?.display,
    patientName: patient.name,
    patientId: patient.id,
    practitionerName: practitioner.name,
    practitionerId: practitioner.id,
    encounterId,
    created: resource.created,
    lastUpdated: resource.meta?.lastUpdated,
  };
}

/**
 * Build search parameters for FHIR query
 */
function buildSearchParams(params: ListAppointmentsParams): Record<string, string> {
  const searchParams: Record<string, string> = {};

  if (params.count !== undefined) {
    searchParams._count = String(params.count);
  }

  if (params.offset !== undefined) {
    searchParams._offset = String(params.offset);
  }

  if (params.sort) {
    searchParams._sort = params.sort;
  }

  if (params.total) {
    searchParams._total = params.total;
  }

  if (params.status) {
    if (Array.isArray(params.status)) {
      searchParams.status = params.status.join(',');
    } else {
      searchParams.status = params.status;
    }
  }

  // dateStart/dateEnd are handled in listAppointments via URLSearchParams.append()
  // to support multiple FHIR date params

  if (params.patient) {
    searchParams.patient = params.patient;
  }

  if (params.practitioner) {
    searchParams.practitioner = params.practitioner;
  }

  // Actor filter (more reliable than practitioner in some Medplum implementations)
  if (params.actor) {
    searchParams.actor = params.actor;
  }

  // End time filter with greater than operator
  if (params.endGt) {
    searchParams.end = `gt${params.endGt}`;
  }

  if (params.serviceType) {
    searchParams['service-type'] = params.serviceType;
  }

  if (params.appointmentType) {
    searchParams['appointment-type'] = params.appointmentType;
  }

  if (params.identifier) {
    searchParams.identifier = params.identifier;
  }

  // FHIR chained search for patient name (e.g., patient.name:contains=John)
  if (params.patientName) {
    searchParams['patient.name:contains'] = params.patientName;
  }

  // FHIR chained search for patient phone
  if (params.patientPhone) {
    const phoneValues = phoneSearchValues(params.patientPhone);
    searchParams['patient.phone'] = phoneValues.length
      ? phoneValues.join(',')
      : params.patientPhone;
  }

  // Note: _include is handled separately in listAppointments via urlParams.append()
  // to correctly support multiple FHIR _include parameters

  return searchParams;
}

/**
 * Appointments API
 */
export const appointmentsApi = {
  /**
   * List appointments with optional filters and pagination
   */
  listAppointments: async (
    params: ListAppointmentsParams = {}
  ): Promise<ListAppointmentsResult> => {
    try {
      logger.log('Listing appointments', params);

      const searchParams = buildSearchParams(params);
      logger.log('Search parameters:', searchParams);

      const urlParams = new URLSearchParams(searchParams);

      // FHIR requires separate date params for range queries
      if (params.dateStart) {
        urlParams.append('date', `ge${params.dateStart}`);
      }
      if (params.dateEnd) {
        urlParams.append('date', `le${params.dateEnd}`);
      }

      // Add _include params separately so each gets its own key (FHIR requirement)
      if (params.include && params.include.length > 0) {
        params.include.forEach((includeValue) => {
          urlParams.append('_include', includeValue);
        });
      }

      const queryString = urlParams.toString();
      const endpoint = `/Appointment${queryString ? `?${queryString}` : ''}`;

      const response = await apiClient.get<AppointmentBundle>(endpoint);
      const bundle = response.data;

      // Parse included resources
      const patients = new Map<string, any>();
      const practitioners = new Map<string, any>();

      if (bundle.entry) {
        bundle.entry.forEach((entry) => {
          const resource = entry.resource as any;
          if (entry.search?.mode === 'include' && resource) {
            if (resource.resourceType === 'Patient' && resource.id) {
              patients.set(resource.id, resource);
            } else if (resource.resourceType === 'Practitioner' && resource.id) {
              practitioners.set(resource.id, resource);
            }
          }
        });
      }

      const appointments =
        bundle.entry
          ?.filter((entry) => entry.search?.mode !== 'include')
          ?.map((entry) => transformAppointmentToListItem(entry.resource as AppointmentResource)) ||
        [];

      const total = bundle.total || appointments.length;
      const count = params.count || 20;
      const offset = params.offset || 0;

      const result: ListAppointmentsResult = {
        appointments,
        total,
        hasNext: offset + count < total,
        hasPrev: offset > 0,
        patients: patients.size > 0 ? patients : undefined,
        practitioners: practitioners.size > 0 ? practitioners : undefined,
      };

      logger.log(`Retrieved ${appointments.length} appointments out of ${total} total`);
      if (patients.size > 0) logger.log(`Included ${patients.size} patients`);
      if (practitioners.size > 0) logger.log(`Included ${practitioners.size} practitioners`);
      return result;
    } catch (error) {
      logger.error('Failed to list appointments:', error);
      throw error;
    }
  },

  /**
   * Get a single appointment by ID
   */
  getAppointment: async (id: string): Promise<AppointmentResource> => {
    try {
      logger.log('Fetching appointment:', id);

      const response = await apiClient.get<AppointmentResource>(`/Appointment/${id}`);
      logger.log('Retrieved appointment:', response.data.id);
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch appointment:', error);
      throw error;
    }
  },

  /**
   * Create a new appointment
   */
  createAppointment: async (request: CreateAppointmentRequest): Promise<AppointmentResource> => {
    try {
      logger.log('Creating appointment', request);

      // FHIR constraint app-2: Either start and end are specified, or neither
      // If start is provided but end is not, calculate end based on duration (default 30 min)
      let endTime = request.end;
      if (request.start && !endTime) {
        const startDate = new Date(request.start);
        const durationMs = (request.minutesDuration || 30) * 60 * 1000;
        endTime = new Date(startDate.getTime() + durationMs).toISOString();
      }

      const appointment: Partial<AppointmentResource> = {
        resourceType: 'Appointment',
        status: request.status,
        start: request.start,
        end: endTime,
        minutesDuration: request.minutesDuration || 30,
        description: request.description,
        comment: request.comment,
        priority: request.priority,
        participant: [
          {
            actor: {
              reference: `Patient/${request.patientId}`,
            },
            status: 'accepted',
          },
          {
            actor: {
              reference: `Practitioner/${request.practitionerId}`,
            },
            status: 'accepted',
          },
        ],
      };

      // Add supportingInformation with Encounter reference if provided
      if (request.encounterId) {
        appointment.supportingInformation = [
          {
            reference: `Encounter/${request.encounterId}`,
          },
        ];
      }

      if (request.appointmentType) {
        appointment.appointmentType = {
          text: request.appointmentType,
        };
      }

      if (request.serviceType) {
        appointment.serviceType = [
          {
            text: request.serviceType,
          },
        ];
      }

      if (request.reasonCode || request.reasonText) {
        appointment.reasonCode = [
          {
            coding: request.reasonCode
              ? [
                  {
                    code: request.reasonCode,
                  },
                ]
              : undefined,
            text: request.reasonText,
          },
        ];
      }

      const response = await apiClient.post<AppointmentResource>('/Appointment', {
        body: appointment,
      });
      logger.log('Created appointment:', response.data.id);
      return response.data;
    } catch (error) {
      logger.error('Failed to create appointment:', error);
      throw error;
    }
  },

  /**
   * Update an existing appointment
   */
  updateAppointment: async (
    id: string,
    request: UpdateAppointmentRequest
  ): Promise<AppointmentResource> => {
    try {
      logger.log('Updating appointment:', id, request);

      const existingResponse = await apiClient.get<AppointmentResource>(`/Appointment/${id}`);
      const existing = existingResponse.data;

      // FHIR constraint app-2: Either start and end are specified, or neither
      // If start is being updated but end is not provided, calculate new end
      let endTime = request.end;
      if (request.start && !endTime) {
        const startDate = new Date(request.start);
        // Use existing duration or default to 30 minutes
        const durationMs = (request.minutesDuration || existing.minutesDuration || 30) * 60 * 1000;
        endTime = new Date(startDate.getTime() + durationMs).toISOString();
      }

      // Extract encounterId and practitionerId from request to handle separately (not valid FHIR properties)
      const { encounterId, practitionerId, ...fhirValidRequest } = request;

      const updated = {
        ...existing,
        ...fhirValidRequest,
        ...(endTime && { end: endTime }),
      };

      if (request.cancelationReason && request.status === 'cancelled') {
        updated.cancelationReason = {
          text: request.cancelationReason,
        };
      }

      // Add supportingInformation with Encounter reference if provided and not already present
      if (encounterId) {
        const existingSupportingInfo = existing.supportingInformation || [];
        const hasEncounter = existingSupportingInfo.some((info) =>
          info.reference?.startsWith('Encounter/')
        );
        if (!hasEncounter) {
          updated.supportingInformation = [
            ...existingSupportingInfo,
            { reference: `Encounter/${encounterId}` },
          ];
        }
      }

      // Update practitioner participant if practitionerId is provided
      if (practitionerId) {
        const existingParticipants = existing.participant || [];
        // Find and update the practitioner participant, or add if not present
        const practitionerIndex = existingParticipants.findIndex((p) =>
          p.actor?.reference?.startsWith('Practitioner/')
        );

        const practitionerParticipant = {
          actor: { reference: `Practitioner/${practitionerId}` },
          status: 'accepted' as const,
        };

        if (practitionerIndex >= 0) {
          // Update existing practitioner participant
          updated.participant = [
            ...existingParticipants.slice(0, practitionerIndex),
            practitionerParticipant,
            ...existingParticipants.slice(practitionerIndex + 1),
          ];
        } else {
          // Add new practitioner participant
          updated.participant = [...existingParticipants, practitionerParticipant];
        }
        logger.log('Updated practitioner to:', practitionerId);
      }

      const response = await apiClient.put<AppointmentResource>(`/Appointment/${id}`, {
        body: updated,
      });
      logger.log('Updated appointment:', response.data.id);
      return response.data;
    } catch (error) {
      logger.error('Failed to update appointment:', error);
      throw error;
    }
  },

  /**
   * Cancel an appointment
   */
  cancelAppointment: async (id: string, reason?: string): Promise<AppointmentResource> => {
    try {
      logger.log('Cancelling appointment:', id);
      return await appointmentsApi.updateAppointment(id, {
        status: 'cancelled',
        cancelationReason: reason,
      });
    } catch (error) {
      logger.error('Failed to cancel appointment:', error);
      throw error;
    }
  },

  /**
   * Get appointments for a specific patient
   */
  getPatientAppointments: async (
    patientId: string,
    params?: Omit<ListAppointmentsParams, 'patient'>
  ): Promise<ListAppointmentsResult> => {
    return appointmentsApi.listAppointments({
      ...params,
      patient: `Patient/${patientId}`,
    });
  },

  /**
   * Get appointments for a specific practitioner
   */
  getPractitionerAppointments: async (
    practitionerId: string,
    params?: Omit<ListAppointmentsParams, 'practitioner'>
  ): Promise<ListAppointmentsResult> => {
    return appointmentsApi.listAppointments({
      ...params,
      practitioner: `Practitioner/${practitionerId}`,
    });
  },

  /**
   * Get upcoming appointments (future appointments only)
   */
  getUpcomingAppointments: async (
    params?: ListAppointmentsParams
  ): Promise<ListAppointmentsResult> => {
    const now = new Date().toISOString();
    return appointmentsApi.listAppointments({
      ...params,
      dateStart: now,
      sort: params?.sort || '_lastUpdated',
      status: params?.status || ['booked', 'arrived', 'pending'],
    });
  },

  /**
   * Get past appointments (historical appointments)
   */
  getPastAppointments: async (params?: ListAppointmentsParams): Promise<ListAppointmentsResult> => {
    const now = new Date().toISOString();
    return appointmentsApi.listAppointments({
      ...params,
      dateEnd: now,
      sort: params?.sort || '-_lastUpdated',
      status: params?.status || ['fulfilled', 'cancelled', 'noshow'],
    });
  },

  /**
   * Get today's appointments
   */
  getTodaysAppointments: async (
    params?: ListAppointmentsParams
  ): Promise<ListAppointmentsResult> => {
    const today = new Date();
    const startOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      0,
      0,
      0,
      0
    ).toISOString();
    const endOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999
    ).toISOString();

    return appointmentsApi.listAppointments({
      ...params,
      dateStart: startOfDay,
      dateEnd: endOfDay,
      sort: params?.sort || 'date',
    });
  },

  /**
   * Get appointment associated with an encounter
   * First tries to get appointment from encounter's appointment reference,
   * then falls back to searching by patient and date
   */
  getAppointmentForEncounter: async (encounterId: string): Promise<AppointmentResource | null> => {
    try {
      logger.log('🔍 Finding appointment for encounter:', encounterId);

      // First get the encounter
      const encounterResponse = await apiClient.get<any>(`/Encounter/${encounterId}`);
      const encounter = encounterResponse.data;
      logger.log('📋 Encounter loaded:', {
        id: encounter.id,
        hasAppointmentRef: !!encounter.appointment,
        appointmentCount: encounter.appointment?.length || 0,
      });

      // Check if encounter has a direct appointment reference
      if (encounter.appointment && encounter.appointment.length > 0) {
        const appointmentRef = encounter.appointment[0].reference;
        logger.log('🔗 Encounter has appointment reference:', appointmentRef);

        if (appointmentRef) {
          const appointmentId = appointmentRef.split('/').pop();
          if (appointmentId) {
            try {
              const appointmentResponse = await apiClient.get<AppointmentResource>(
                `/Appointment/${appointmentId}`
              );
              logger.log(
                '✅ Found appointment from encounter reference:',
                appointmentResponse.data.id
              );
              return appointmentResponse.data;
            } catch (error) {
              logger.error('❌ Failed to read appointment from reference:', error);
              // Continue to fallback search
            }
          }
        }
      } else {
        logger.log('ℹ️ Encounter has no appointment reference, trying fallback search...');
      }

      // Fallback: Search for appointments based on patient and date (date-only, not full ISO)
      const patientRef = encounter.subject?.reference;
      const encounterDate = encounter.period?.start;

      if (!patientRef || !encounterDate) {
        logger.log('⚠️ Encounter missing patient or date information:', {
          hasPatient: !!patientRef,
          hasDate: !!encounterDate,
        });
        return null;
      }

      // Use date-only (YYYY-MM-DD) for broader matching — full ISO timestamps
      // almost never match the appointment's start time exactly
      const dateOnly = encounterDate.split('T')[0];

      logger.log('🔍 Searching for appointment by patient and date...');
      logger.log('Search params:', { patient: patientRef, date: dateOnly });

      const searchParams = {
        patient: patientRef,
        date: dateOnly,
        _count: '10',
      };

      const queryString = new URLSearchParams(searchParams).toString();
      const bundleResponse = await apiClient.get<AppointmentBundle>(`/Appointment?${queryString}`);
      const bundle = bundleResponse.data;
      logger.log('Search results:', { entryCount: bundle.entry?.length || 0 });

      if (bundle.entry && bundle.entry.length > 0) {
        // Return the first matching appointment
        const appointment = bundle.entry[0].resource as AppointmentResource;
        logger.log(
          '✅ Found appointment via search:',
          appointment.id,
          'Status:',
          appointment.status
        );
        return appointment;
      }

      logger.log('❌ No appointment found for encounter');
      return null;
    } catch (error) {
      logger.error('❌ Failed to find appointment for encounter:', error);
      return null;
    }
  },

  /**
   * Mark appointment as fulfilled
   */
  fulfillAppointment: async (id: string): Promise<AppointmentResource> => {
    try {
      logger.log('Marking appointment as fulfilled:', id);
      return await appointmentsApi.updateAppointment(id, {
        status: 'fulfilled',
      });
    } catch (error) {
      logger.error('Failed to fulfill appointment:', error);
      throw error;
    }
  },

  /**
   * Delete an appointment
   * Used for rollback operations when appointment creation fails
   */
  deleteAppointment: async (id: string): Promise<void> => {
    try {
      logger.log('Deleting appointment:', id);
      await apiClient.delete(`/Appointment/${id}`);
      logger.log('Appointment deleted successfully:', id);
    } catch (error) {
      logger.error('Failed to delete appointment:', error);
      throw error;
    }
  },

  /**
   * Check if a patient is new (has never had an appointment before today)
   * @param patientId - Patient resource ID (e.g., 'Patient/123' or just '123')
   * @returns true if the patient has no prior appointments before today
   */
  checkPatientIsNew: async (patientId: string): Promise<boolean> => {
    try {
      const normalizedId = patientId.replace(/^Patient\//, '');
      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0,
        0
      );

      // Check for any appointments before today
      const result = await appointmentsApi.listAppointments({
        patient: `Patient/${normalizedId}`,
        dateEnd: todayStart.toISOString(),
        count: 1, // Only need to know if at least one exists
      });

      const isNew = result.total === 0;
      logger.log(`Patient ${normalizedId} is ${isNew ? 'NEW' : 'EXISTING'}`);
      return isNew;
    } catch (error) {
      logger.error('Failed to check if patient is new:', error);
      // Default to false (existing patient) on error to be safe
      return false;
    }
  },

  /**
   * Check multiple patients at once to determine if they are new
   * Optimized: Uses a single batched query instead of one call per patient
   * @param patientIds - Array of patient IDs to check
   * @returns Map of patient ID to boolean (true = new patient)
   */
  checkPatientsAreNew: async (patientIds: string[]): Promise<Map<string, boolean>> => {
    const patientIsNewMap = new Map<string, boolean>();

    if (patientIds.length === 0) {
      return patientIsNewMap;
    }

    try {
      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        0,
        0,
        0,
        0
      );

      // Normalize all patient IDs
      const normalizedIds = patientIds.map((id) => id.replace(/^Patient\//, ''));

      // Single batched query: fetch appointments before today filtered by the requested patients
      const patientFilter = normalizedIds.map((id) => `Patient/${id}`).join(',');
      const result = await appointmentsApi.listAppointments({
        patient: patientFilter,
        dateEnd: todayStart.toISOString(),
        count: 1000,
        status: [
          'booked',
          'pending',
          'proposed',
          'arrived',
          'fulfilled',
          'cancelled',
          'noshow',
          'checked-in',
          'waitlist',
        ],
      });

      // Build a set of patient IDs who have appointments before today
      const patientsWithHistory = new Set<string>();
      result.appointments.forEach((apt) => {
        if (apt.patientId) {
          const normalizedId = apt.patientId.replace(/^Patient\//, '');
          patientsWithHistory.add(normalizedId);
        }
      });

      // Determine which patients are new (not in the history set)
      normalizedIds.forEach((patientId) => {
        patientIsNewMap.set(patientId, !patientsWithHistory.has(patientId));
      });

      logger.log(`Batch checked ${patientIds.length} patients: ${patientIsNewMap.size} results`);
      return patientIsNewMap;
    } catch (error) {
      logger.error('Failed to check patients are new:', error);
      // Default all to false (existing) on error
      patientIds.forEach((id) => {
        const normalizedId = id.replace(/^Patient\//, '');
        patientIsNewMap.set(normalizedId, false);
      });
      return patientIsNewMap;
    }
  },
};

export default appointmentsApi;
